import { useCallback, useId, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Text } from '../Text/Text';
import styles from './Slider.module.css';

/**
 * Figma: `Slider` (2098:5928) and `_Control handle` (2098:6109) — a dual-handle
 * range control whose two handles are the boundaries between three zones:
 * negative on the left, neutral in the middle, positive on the right.
 *
 * Figma models the position as two variant properties, `Left control`
 * (25%|50%) and `Right control` (50%|75%), because a variant cannot hold a
 * continuous value. That makes the four combinations an authoring limitation
 * rather than the model — the same call `ProgressBar` makes about its eleven
 * 10% steps — so this takes a `[lower, upper]` pair and derives the geometry.
 *
 * Neither component set carries annotations, so the intent below comes from the
 * bound tokens and the geometry rather than from the designer.
 *
 * Known drift in the design file, not reproduced here — see plan.md §7:
 *   - Every handle sits 4px left of the percentage it is labelled with. All six
 *     are off by the same amount, so it is a hand-placement artifact rather than
 *     a designed inset; handles here centre on their true value.
 *   - The red `Background` rect is 84, 94 and 160 wide across three variants
 *     whose left control is 25%, 25% and 50% — it should be 80, 80, 160. It is
 *     drawn slightly long on purpose, to back-fill the amber segment's rounded
 *     left cap, but the overshoot is inconsistent. This clips the segments to a
 *     pill instead, which needs no overlap.
 *   - `State=Hover` is pixel-identical to `State=Default` on all three handle
 *     types. There is no designed hover, so none is invented here.
 *   - The set has no `Disabled` variant, so `disabled` changes behaviour only.
 */

/** `[lower, upper]` — the negative/neutral and neutral/positive boundaries. */
export type SliderValue = readonly [number, number];

/** Figma `_Control handle` `Type`. `False` there is `none` here. */
export type SliderHandleLabel = 'none' | 'tooltip' | 'text';

export type SliderProps = {
  /** Controlled `[lower, upper]`. Omit to let the component own the value. */
  value?: SliderValue;
  /**
   * Uncontrolled starting pair. Defaults to even thirds, which gives the
   * neutral band the same weight as each pole.
   */
  defaultValue?: SliderValue;
  onChange?: (value: SliderValue) => void;
  /** Fires once when a drag ends, for committing rather than previewing. */
  onChangeEnd?: (value: SliderValue) => void;
  min?: number;
  max?: number;
  /** Arrow keys and drags land on multiples of this, measured from `min`. */
  step?: number;
  /**
   * Smallest neutral band the handles may be dragged to. Defaults to 5, which
   * on the design's 320x6 track is exactly the handle's own width — below that
   * the two 16px handles overlap and the narrower one becomes hard to grab.
   */
  minNeutralWidth?: number;
  /** Figma `_Control handle` `Type` — applies to both handles. */
  handleLabel?: SliderHandleLabel;
  /** Renders the number beside a handle. Defaults to `25%` formatting. */
  formatValue?: (value: number) => string;
  /**
   * Behaviour only. Figma has no `Disabled` variant, so a disabled slider still
   * draws as the default one; it just stops responding.
   */
  disabled?: boolean;
  /**
   * Accessible names for the two handles. Not in Figma — a bare handle names
   * nothing, and a dual slider needs the two announced apart.
   */
  labels?: { lower?: string; upper?: string };
  /** Submitted as `<name>-lower` and `<name>-upper` when inside a form. */
  name?: string;
  id?: string;
  className?: string;
};

const DEFAULT_VALUE: SliderValue = [33, 66];

const clamp = (n: number, low: number, high: number) => Math.min(high, Math.max(low, n));

/** Float noise from `x / step * step` shows up in the label, so trim it. */
const tidy = (n: number) => Math.round(n * 1e6) / 1e6;

export function Slider({
  value: valueProp,
  defaultValue = DEFAULT_VALUE,
  onChange,
  onChangeEnd,
  min = 0,
  max = 100,
  step = 1,
  minNeutralWidth = 5,
  handleLabel = 'tooltip',
  formatValue = (n) => `${n}%`,
  disabled = false,
  labels,
  name,
  id,
  className,
}: SliderProps) {
  const generatedId = useId();
  const rootId = id ?? generatedId;
  const railRef = useRef<HTMLDivElement>(null);
  const lowerInputRef = useRef<HTMLInputElement>(null);
  const upperInputRef = useRef<HTMLInputElement>(null);
  const draggingRef = useRef<0 | 1 | null>(null);

  const [internal, setInternal] = useState<SliderValue>(defaultValue);
  const value = valueProp ?? internal;

  /* A gap wider than the axis would leave no legal position for either handle. */
  const gap = clamp(minNeutralWidth, 0, max - min);

  const span = max - min || 1;
  const lowerPct = ((clamp(value[0], min, max) - min) / span) * 100;
  const upperPct = ((clamp(value[1], min, max) - min) / span) * 100;

  const apply = useCallback(
    (index: 0 | 1, raw: number) => {
      const snapped = tidy(min + Math.round((raw - min) / step) * step);
      const next: SliderValue =
        index === 0
          ? [clamp(snapped, min, value[1] - gap), value[1]]
          : [value[0], clamp(snapped, value[0] + gap, max)];

      if (next[0] === value[0] && next[1] === value[1]) return value;
      if (valueProp === undefined) setInternal(next);
      onChange?.(next);
      return next;
    },
    [gap, max, min, onChange, step, value, valueProp],
  );

  /** Ratio of the *rail*, not the root — the root is taller than the track. */
  const valueAt = (clientX: number): number | null => {
    const rect = railRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return null;
    return min + ((clientX - rect.left) / rect.width) * span;
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    const raw = valueAt(event.clientX);
    if (raw === null) return;

    /*
     * Nearest handle wins. On an exact tie — which happens whenever the neutral
     * band is at its minimum — pick the handle the pointer is on the outside
     * of, so the gesture opens the band rather than refusing to move.
     */
    const toLower = Math.abs(raw - value[0]);
    const toUpper = Math.abs(raw - value[1]);
    const index: 0 | 1 = toLower === toUpper ? (raw < value[0] ? 0 : 1) : toLower < toUpper ? 0 : 1;

    draggingRef.current = index;
    /*
     * Capture keeps the drag alive once the pointer leaves the 24px band, which
     * matters because the handle can be dragged well past either end. It is an
     * enhancement, not a requirement — the move and up handlers sit on the same
     * element either way — and it is specified to throw if the pointer is no
     * longer active, which a fast click or a synthesised event can produce.
     */
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      /* Drag still works while the pointer stays over the root. */
    }
    (index === 0 ? lowerInputRef : upperInputRef).current?.focus();
    apply(index, raw);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const index = draggingRef.current;
    if (index === null) return;
    const raw = valueAt(event.clientX);
    if (raw !== null) apply(index, raw);
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (draggingRef.current === null) return;
    draggingRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    onChangeEnd?.(value);
  };

  /*
   * Each handle's own min/max carries the neutral-band rule, so the native input
   * enforces it for arrow keys, Home/End and Page Up/Down without a keydown
   * handler — and assistive technology announces the range the handle can
   * actually reach rather than the full axis.
   */
  const bounds: Array<{ min: number; max: number }> = [
    { min, max: tidy(value[1] - gap) },
    { min: tidy(value[0] + gap), max },
  ];

  const renderControl = (index: 0 | 1) => {
    const current = value[index];
    const percent = index === 0 ? lowerPct : upperPct;
    const label =
      index === 0 ? (labels?.lower ?? 'Negative threshold') : (labels?.upper ?? 'Positive threshold');

    return (
      <span
        key={index}
        className={styles.control}
        /* The only value that cannot be a token: it is the datum itself. */
        style={{ left: `${percent}%` }}
      >
        {/*
         * Not in Figma — Figma draws a circle, not a control. A real range input
         * gives keyboard operation, form participation and an announced value
         * for free. It is transparent and inert to the pointer: dragging is
         * handled on the root so that a press anywhere picks the nearer handle,
         * which a native input cannot do.
         */}
        <input
          ref={index === 0 ? lowerInputRef : upperInputRef}
          className={styles.input}
          type="range"
          id={`${rootId}-${index === 0 ? 'lower' : 'upper'}`}
          name={name === undefined ? undefined : `${name}-${index === 0 ? 'lower' : 'upper'}`}
          min={bounds[index].min}
          max={bounds[index].max}
          step={step}
          value={current}
          disabled={disabled}
          aria-label={label}
          aria-valuetext={formatValue(current)}
          onChange={(event) => apply(index, Number(event.target.value))}
          onKeyUp={() => onChangeEnd?.(value)}
        />

        <span className={styles.handle} aria-hidden="true" />

        {handleLabel === 'tooltip' && (
          /* Figma `Type=Tooltip`. Not the `Tooltip` atom — that one is the dark
             `surface/inverse` bubble with an arrow; this is a light `surface/card`
             pill at `radius/sm` with `shadow/lg` and no arrow. Redundant with
             `aria-valuetext`, so it is hidden from the accessibility tree. */
          <span className={styles.tooltip} aria-hidden="true">
            <Text size="sm" leading="none" weight="regular" color="secondary">
              {formatValue(current)}
            </Text>
          </span>
        )}

        {handleLabel === 'text' && (
          /* Figma `Type=Text` — the value sits under the handle instead, a step
             heavier and darker: `Text sm/leading-normal/Medium` at text/primary. */
          <span className={styles.valueText} aria-hidden="true">
            <Text size="sm" leading="normal" weight="medium" color="primary">
              {formatValue(current)}
            </Text>
          </span>
        )}
      </span>
    );
  };

  return (
    <div
      className={[styles.root, disabled ? styles.disabled : null, className]
        .filter(Boolean)
        .join(' ')}
      id={rootId}
      /* Dragging is owned by the root, not the 6px rail, so the whole 24px band
         is grabbable. `touch-action` is what stops a drag scrolling the page. */
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <div className={styles.rail} ref={railRef}>
        {/*
         * Figma stacks three full-height rectangles, each at radius/full, and
         * overlaps them so the upper one's rounded cap is backed by the lower.
         * Clipping square segments to one pill is the same picture with no
         * overlap to keep in sync, and it stays correct when a band is zero.
         */}
        <div className={styles.track} aria-hidden="true">
          <span
            className={`${styles.segment} ${styles.negative}`}
            style={{ width: `${lowerPct}%` }}
          />
          <span
            className={`${styles.segment} ${styles.neutral}`}
            style={{ left: `${lowerPct}%`, width: `${upperPct - lowerPct}%` }}
          />
          <span className={`${styles.segment} ${styles.positive}`} style={{ left: `${upperPct}%` }} />
        </div>

        {renderControl(0)}
        {renderControl(1)}
      </div>
    </div>
  );
}

/**
 * Which zone a value falls in, given a slider pair. The boundaries belong to the
 * zone above them: at `lower` a value is neutral, at `upper` it is positive.
 * Exported because the whole point of the control is to classify something.
 */
export type SliderZone = 'negative' | 'neutral' | 'positive';

export function sliderZone(value: number, [lower, upper]: SliderValue): SliderZone {
  if (value < lower) return 'negative';
  if (value < upper) return 'neutral';
  return 'positive';
}
